import { MGPOptional, MGPValidation, Utils } from '@everyboard/lib';
import { NumberConfig } from '../../config/NumberConfig';
import { RulesConfigDescription } from '../../config/RulesConfigDescription';
import { RulesConfigDescriptionLocalizable } from '../../config/RulesConfigDescriptionLocalizable';
import { GameNode } from '../../jscaip/AI/GameNode';
import { CoordFailure } from '../../jscaip/Coord';
import { GameStatus } from '../../jscaip/GameStatus';
import { NInARowHelper } from '../../jscaip/NInARowHelper';
import { Ordinal } from '../../jscaip/Ordinal';
import { PlayerOrNone } from '../../jscaip/Player';
import { PlayerNumberMap } from '../../jscaip/PlayerMap';
import { ConfigurableRules } from '../../jscaip/Rules';
import { RulesFailure } from '../../jscaip/RulesFailure';
import { TableUtils } from '../../jscaip/TableUtils';
import { MGPValidators } from '../../utils/MGPValidator';
import { PenteState } from './PenteState';
export class PenteNode extends GameNode {
}
export class PenteRules extends ConfigurableRules {
    static RULES_CONFIG_DESCRIPTION = new RulesConfigDescription({
        name: () => $localize `Default`,
        config: {
            width: new NumberConfig(19, RulesConfigDescriptionLocalizable.WIDTH, MGPValidators.range(1, 99)),
            height: new NumberConfig(19, RulesConfigDescriptionLocalizable.HEIGHT, MGPValidators.range(1, 99)),
            capturesNeededToWin: new NumberConfig(10, () => $localize `Captured stones needed to win`, MGPValidators.range(1, 123456)),
            nInARow: new NumberConfig(5, RulesConfigDescriptionLocalizable.ALIGNMENT_SIZE, MGPValidators.range(3, 99)),
            sizeOfSandwich: new NumberConfig(2, () => $localize `Size of captures`, MGPValidators.range(1, 99)),
        },
    });
    static singleton = MGPOptional.empty();
    static get() {
        if (PenteRules.singleton.isAbsent()) {
            PenteRules.singleton = MGPOptional.of(new PenteRules());
        }
        return PenteRules.singleton.get();
    }
    getInitialState(config) {
        const board = TableUtils.create(config.width, config.height, PlayerOrNone.NONE);
        const cx = Math.floor(config.width / 2);
        const cy = Math.floor(config.height / 2);
        board[cy][cx] = PlayerOrNone.ONE;
        return new PenteState(board, PlayerNumberMap.of(0, 0), 0);
    }
    getRulesConfigDescription() {
        return PenteRules.RULES_CONFIG_DESCRIPTION;
    }
    isLegal(move, state) {
        if (state.isNotOnBoard(move.coord)) {
            return MGPValidation.failure(CoordFailure.OUT_OF_RANGE(move.coord));
        }
        else if (state.getPieceAt(move.coord).isPlayer()) {
            return MGPValidation.failure(RulesFailure.MUST_CLICK_ON_EMPTY_SQUARE());
        }
        else {
            return MGPValidation.SUCCESS;
        }
    }
    applyLegalMove(move, state, config, _info) {
        const player = state.getCurrentPlayer();
        const newBoard = state.getCopiedBoard();
        newBoard[move.coord.y][move.coord.x] = player;
        const capturedPieces = this.getCaptures(move.coord, state, config, player);
        for (const captured of capturedPieces) {
            newBoard[captured.y][captured.x] = PlayerOrNone.NONE;
        }
        const captures = state.captures.getCopy();
        captures.add(player, capturedPieces.length);
        return new PenteState(newBoard, captures, state.turn + 1);
    }
    getCaptures(coord, state, config, player) {
        const opponent = player.getOpponent();
        const captures = [];
        const sizeOfCapture = config.sizeOfSandwich;
        for (const direction of Ordinal.factory.all) {
            let i = 1;
            let potentialCapture = coord.getNext(direction, i);
            const captured = [potentialCapture];
            while (state.hasPieceAt(potentialCapture, opponent) && i < sizeOfCapture) {
                i++;
                potentialCapture = potentialCapture.getNext(direction, 1);
                captured.push(potentialCapture);
            }
            const sandwicher = coord.getNext(direction, sizeOfCapture + 1);
            if (state.hasPieceAt(potentialCapture, opponent) &&
                i === sizeOfCapture &&
                state.hasPieceAt(sandwicher, player)) {
                captures.push(...captured);
            }
        }
        return captures;
    }
    getGameStatus(node, config) {
        const state = node.gameState;
        const opponent = state.getCurrentOpponent();
        const capturesNeededToWin = config.capturesNeededToWin;
        if (capturesNeededToWin <= state.captures.get(opponent)) {
            return GameStatus.getVictory(opponent);
        }
        const victoriousCoord = this.getHelper(config).getVictoriousCoord(state);
        if (victoriousCoord.length > 0) {
            return GameStatus.getVictory(opponent);
        }
        if (this.stillHaveEmptySquare(state)) {
            return GameStatus.ONGOING;
        }
        else {
            return GameStatus.DRAW;
        }
    }
    getHelper(config) {
        return new NInARowHelper(Utils.identity, config.nInARow);
    }
    stillHaveEmptySquare(state) {
        const width = state.getWidth();
        const height = state.getHeight();
        for (let y = 0; y < height; y++) {
            for (let x = 0; x < width; x++) {
                if (state.board[y][x].isNone()) {
                    return true;
                }
            }
        }
        return false;
    }
}
